import { useState, useEffect } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { Menu, Building2, MapPin, ChevronDown, LogOut, Key, ShieldCheck } from "lucide-react";
import { useAuthStore } from "../../store/authStore";
import { Sidebar } from "./Sidebar";
import { Button } from "../ui/Button";
import { outletsApi } from "../../api/outlets";
import type { Outlet as OutletType } from "../../types/models";
import { ChangePasswordModal } from "../ui/ChangePasswordModal";
import { LanguageSelector } from "../ui/LanguageSelector";
import { NotificationBell } from "./NotificationBell";
import { useTranslation } from "react-i18next";

export function AppShell() {
  const { t } = useTranslation();
  const [sidebarOpen, setSidebarOpen] = useState(() => 
    typeof window !== "undefined" ? window.innerWidth >= 1024 : true
  );
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const navigate = useNavigate();
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1024) {
        setSidebarOpen(false);
      } else {
        setSidebarOpen(true);
      }
    };
    
    // Initial check on mount
    handleResize();

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const {
    currentBusiness,
    currentOutlet,
    allBusinesses,
    user,
    switchBusiness,
    setCurrentOutlet,
    logout,
  } = useAuthStore();

  const [outlets, setOutlets] = useState<OutletType[]>([]);

  const handleBusinessChange = async (businessId: string) => {
    const business = allBusinesses?.find((b) => b.id === businessId);
    if (business) {
      try {
        await switchBusiness(businessId);
        // Load outlets for the selected business
        const fetchedOutlets = await outletsApi.listOutlets(businessId);
        setOutlets(fetchedOutlets);
      } catch (error) {
        console.error("Failed to switch business context:", error);
      }
    }
  };

  const handleOutletChange = (outletId: string) => {
    const outlet = outlets.find((o) => o.id === outletId);
    if (outlet) {
      setCurrentOutlet(outlet);
    }
  };

  return (
    <div className="flex h-screen bg-surface-alt print:block print:h-auto print:bg-white">
      {/* Sidebar */}
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        onToggle={() => setSidebarOpen(!sidebarOpen)}
        currentBusiness={currentBusiness}
        currentOutlet={currentOutlet}
        outlets={outlets}
        onBusinessChange={handleBusinessChange}
        onOutletChange={handleOutletChange}
        onOutletsLoad={setOutlets}
      />

      {/* Main content */}
      {/* A printed page is not a viewport. h-screen + overflow made this a
          clipped scroll container, so printing any page produced the fixed
          sidebar and nothing else — the content was outside the printable
          flow entirely. Print unwinds the whole chain. */}
      <div className="flex-1 flex flex-col overflow-hidden print:block print:overflow-visible">
        {/* Topbar */}
        <header className="h-14 bg-surface border-b border-border flex items-center justify-between px-4 print:hidden">
          <div className="flex items-center gap-3 min-w-0">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              icon={<Menu className="h-5 w-5" />}
            />

            {/* Where you are. A phone has room for one name, so it shows the
                outlet (the business when there is none), cut short with an
                ellipsis; from sm up, both. */}
            <div className="flex items-center gap-2 text-sm min-w-0">
              {currentBusiness && (
                <span className={`${currentOutlet ? "hidden sm:flex" : "flex"} items-center gap-2 min-w-0`}>
                  <Building2 className="h-4 w-4 text-text-muted shrink-0" />
                  <span className="text-text-muted truncate">
                    {currentBusiness.name}
                  </span>
                </span>
              )}
              {currentOutlet && (
                <>
                  <ChevronDown className="h-4 w-4 text-text-muted shrink-0 hidden sm:block" />
                  <MapPin className="h-4 w-4 text-text-muted shrink-0" />
                  <span className="text-text-muted truncate">{currentOutlet.name}</span>
                </>
              )}
            </div>
          </div>

          {/* Language and User menus */}
          <div className="flex items-center gap-1 sm:gap-4 shrink-0">
            <NotificationBell />

            <LanguageSelector />

            {/* User menu */}
            <div className="flex items-center gap-3 relative">
              {userMenuOpen && (
                <div 
                  className="fixed inset-0 z-45 cursor-default" 
                  onClick={() => setUserMenuOpen(false)} 
                />
              )}

            
            <button
              onClick={() => setUserMenuOpen(!userMenuOpen)}
              className="flex items-center gap-2 hover:bg-sidebar-hover/10 px-2 py-1.5 rounded-md transition-colors"
            >
              <div className="h-8 w-8 rounded-full bg-brand text-white flex items-center justify-center text-sm font-medium">
                {user?.name?.[0]?.toUpperCase() || "U"}
              </div>
              <span className="text-sm text-text hidden sm:block font-medium">
                {user?.name || user?.email}
              </span>
              <ChevronDown className={`h-4 w-4 text-text-muted transition-transform duration-200 ${userMenuOpen ? 'rotate-180' : ''}`} />
            </button>

            {userMenuOpen && (
              <div className="absolute right-0 top-full mt-2 w-48 bg-surface border border-border rounded-md shadow-lg z-50 py-1 origin-top-right animate-in fade-in slide-in-from-top-1 duration-100">
                <div className="px-4 py-2 border-b border-border">
                  <p className="text-xs text-text-muted">{t("appShell.signedInAs")}</p>
                  <p className="text-sm font-medium text-text truncate">{user?.name || user?.email}</p>
                </div>
                
                <button
                  onClick={() => {
                    setUserMenuOpen(false);
                    setChangePasswordOpen(true);
                  }}
                  className="w-full flex items-center gap-2 px-4 py-2 text-sm text-text hover:bg-sidebar-hover/10 text-left transition-colors"
                >
                  <Key className="h-4 w-4 text-text-muted" />
                  <span>{t("appShell.changePassword")}</span>
                </button>

                <button
                  onClick={() => {
                    setUserMenuOpen(false);
                    navigate("/account/two-step");
                  }}
                  className="w-full flex items-center gap-2 px-4 py-2 text-sm text-text hover:bg-sidebar-hover/10 text-left transition-colors"
                >
                  <ShieldCheck className="h-4 w-4 text-text-muted" />
                  <span>{t("appShell.twoStep")}</span>
                </button>
                
                <button
                  onClick={() => {
                    setUserMenuOpen(false);
                    logout();
                  }}
                  className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-950/20 text-left transition-colors"
                >
                  <LogOut className="h-4 w-4" />
                  <span>{t("appShell.logOut")}</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </header>



        {/* Page content */}
        <main className="flex-1 overflow-y-auto p-4 pb-[calc(1rem+env(safe-area-inset-bottom))] print:overflow-visible print:p-0 print:pb-0">
          <Outlet />
        </main>

      </div>

      <ChangePasswordModal
        isOpen={changePasswordOpen}
        onClose={() => setChangePasswordOpen(false)}
      />
    </div>
  );
}
